/**
 * The game socket client — the other half of `api/src/ws/gateway.ts`.
 *
 * The protocol is deliberately small: authenticate, subscribe, then listen.
 * There is no frame this client can send that changes the game, because the
 * server offers none — marking and win detection are server-side (§0.6), so a
 * client that thinks it has bingo is a client that is about to be told.
 *
 * **Reconnection is the whole design problem.** A phone on mobile data will
 * drop this socket, and the events missed during the gap are gone: the bus is
 * `LISTEN`/`NOTIFY` and does not replay. That is survivable only because
 * subscribing returns a *snapshot* — the round plus the full draw log — so a
 * reconnecting client throws away what it thought it knew and takes the
 * server's version. §4 makes `called_numbers` the client's only source of
 * truth; this is that sentence implemented.
 *
 * So: never merge a snapshot into local state. Replace it.
 */

import { getInitData } from '../telegram.ts';
import type { CardLayout } from '../api/types.ts';

export interface RoundSnapshotFrame {
  type: 'round.snapshot';
  round: import('../api/types.ts').RoundView;
  calledNumbers: number[];
  cards: import('../api/types.ts').CardView[];
}

export interface RoundStartingFrame {
  type: 'round.starting';
  roundId: string;
  /** ISO. When the countdown ends and the round actually goes live. */
  startsAt: string;
  playerCount: number;
}

export interface RoundStartedFrame {
  type: 'round.started';
  roundId: string;
  seedCommitment: string;
  prizePool: string;
  playerCount: number;
  drawIntervalMs: number;
}

export interface RoundDrawFrame {
  type: 'round.draw';
  roundId: string;
  number: number;
  drawIndex: number;
}

export interface RoundWinnerFrame {
  userId: string;
  cardId: string;
  entryIndex: number;
  amount: string;
  firstName: string;
  /** The winning card, revealed now that the round is over. */
  layout: CardLayout;
  /** Cell indices (0-24) of the specific combo that completed the pattern. */
  winningCombo: number[];
}

export interface RoundFinishedFrame {
  type: 'round.finished';
  roundId: string;
  winner: RoundWinnerFrame;
  /** Set only on a split-pot round (§4 dual winner) where a second card also won. */
  secondWinner?: RoundWinnerFrame;
  rngSeed: string | null;
}

export interface RoundCancelledFrame {
  type: 'round.cancelled';
  roundId: string;
  reason: string;
}

export interface RoundPlayerJoinedFrame {
  type: 'round.player_joined';
  roundId: string;
  playerCount: number;
  cardCount: number;
  /** The picker number just bought, when the purchase went through the picker. */
  cartelaNumber?: number;
}

export type ServerFrame =
  | { type: 'hello'; authTimeoutMs: number }
  | { type: 'auth.ok'; userId: string }
  | { type: 'pong' }
  | { type: 'unsubscribed'; roundId: string }
  | { type: 'error'; code: string; message: string }
  | RoundSnapshotFrame
  | RoundStartingFrame
  | RoundStartedFrame
  | RoundDrawFrame
  | RoundFinishedFrame
  | RoundCancelledFrame
  | RoundPlayerJoinedFrame;

export type ConnectionState = 'connecting' | 'connected' | 'offline';

export interface GameSocketHandlers {
  onFrame(frame: ServerFrame): void;
  onStateChange?(state: ConnectionState): void;
}

const HEARTBEAT_MS = 25_000;
const MAX_BACKOFF_MS = 15_000;

function socketUrl(): string {
  const base = import.meta.env['VITE_WS_URL'] as string | undefined;
  if (base) return base;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

export class GameSocket {
  private socket: WebSocket | null = null;
  private readonly handlers: GameSocketHandlers;
  /** Rounds to (re)subscribe to. Survives a reconnect; the socket does not. */
  private readonly subscriptions = new Set<string>();
  private authenticated = false;
  private closed = false;
  private attempt = 0;
  private reconnectTimer: number | null = null;
  private heartbeat: number | null = null;

  constructor(handlers: GameSocketHandlers) {
    this.handlers = handlers;
  }

  connect(): void {
    if (this.closed || this.socket) return;
    this.setState('connecting');

    const socket = new WebSocket(socketUrl());
    this.socket = socket;
    this.authenticated = false;

    socket.onopen = () => {
      // Nothing is sent until the server says hello — it expects the auth frame
      // first, and a subscribe sent early closes the connection with 4401.
    };

    socket.onmessage = (event) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data as string) as ServerFrame;
      } catch {
        return;
      }

      if (frame.type === 'hello') {
        const initData = getInitData();
        if (!initData) {
          this.close();
          return;
        }
        this.send({ type: 'auth', initData });
        return;
      }

      if (frame.type === 'auth.ok') {
        this.authenticated = true;
        this.attempt = 0;
        this.setState('connected');
        // Re-subscribe to everything this client was watching. Each returns a
        // fresh snapshot, which is how a gap is repaired.
        for (const roundId of this.subscriptions) {
          this.send({ type: 'subscribe', roundId });
        }
        this.startHeartbeat();
        return;
      }

      this.handlers.onFrame(frame);
    };

    socket.onclose = () => {
      this.stopHeartbeat();
      this.socket = null;
      this.authenticated = false;
      if (this.closed) return;
      this.setState('offline');
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // `onclose` always follows, and that is where reconnection is handled.
      // Doing it here too would schedule two reconnects for one failure.
    };
  }

  /**
   * Watch a round. Safe to call before the socket is up — the id is remembered
   * and sent as soon as there is an authenticated connection.
   */
  subscribe(roundId: string): void {
    this.subscriptions.add(roundId);
    if (this.authenticated) this.send({ type: 'subscribe', roundId });
  }

  unsubscribe(roundId: string): void {
    this.subscriptions.delete(roundId);
    if (this.authenticated) this.send({ type: 'unsubscribe', roundId });
  }

  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
  }

  private send(payload: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  /**
   * Exponential backoff with jitter.
   *
   * The jitter matters more here than in most clients: when the API restarts,
   * every player's socket drops at the same instant, and a fixed delay would
   * bring them all back simultaneously — a thundering herd against a server
   * that has only just come up.
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const base = Math.min(500 * 2 ** this.attempt, MAX_BACKOFF_MS);
    const delay = base * (0.5 + Math.random() * 0.5);
    this.attempt++;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /**
   * Application-level ping.
   *
   * A webview that has been backgrounded often has a socket the OS quietly
   * stopped delivering to; without traffic, the tab believes it is connected
   * and the board silently stops updating. Well under the server's token-bucket
   * refill, so the heartbeat can never be what trips the rate limiter.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = window.setInterval(() => this.send({ type: 'ping' }), HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      window.clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  private setState(state: ConnectionState): void {
    this.handlers.onStateChange?.(state);
  }
}
